import { useState } from "react";
import { ChevronRightIcon } from "lucide-react";
import { Link } from "react-router";

import { Center, ErrorText, LoadingIndicator, Text } from "@/components";
import { Pin2Icon } from "@/components/streamline";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { usePinnedJobs } from "@/contexts/PinnedJobsContext";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

export const PinnedJobsSidebarMenuItem = ({
  className,
}: {
  className?: string;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    queryResult: {
      data: pinnedJobsData,
      isPending: isPinnedJobsPending,
      isError: isPinnedJobsError,
    },
  } = usePinnedJobs();

  /* Misc Hooks */
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();

  /* React Hooks */
  const [pinnedJobsCollapsibleOpen, setPinnedJobsCollapsibleOpen] =
    useState(true);

  /* Handlers */
  const handleToggle = () => {
    mixpanelUtils.trackButtonClick(
      "Pinned Jobs Sidebar Toggle",
      getScreenFromPathname(location.pathname),
    );
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setPinnedJobsCollapsibleOpen(true);
    } else {
      setPinnedJobsCollapsibleOpen(!pinnedJobsCollapsibleOpen);
    }
  };

  const handlePinnedJobClick = () => {
    mixpanelUtils.trackNavClick(
      MIXPANEL_SCREENS.JOB,
      getScreenFromPathname(location.pathname),
    );
  };

  /* Helpers */
  // Sort pinned jobs by company abbreviation, then title.
  const pinnedJobs = [...(pinnedJobsData ?? [])].sort((a, b) => {
    const abbrCompare = (a.companyAbbreviation ?? "").localeCompare(
      b.companyAbbreviation ?? "",
    );
    if (abbrCompare !== 0) return abbrCompare;
    return a.title.localeCompare(b.title);
  });

  /* Render */
  const renderListContent = () => {
    if (isPinnedJobsPending) {
      return (
        <SidebarMenuSubItem>
          <Center className="py-2">
            <LoadingIndicator variant="small" />
          </Center>
        </SidebarMenuSubItem>
      );
    }
    if (isPinnedJobsError) {
      return (
        <SidebarMenuSubItem>
          <Center className="px-2 py-2">
            <ErrorText className="text-xs" errorMessage="Failed to load jobs" />
          </Center>
        </SidebarMenuSubItem>
      );
    }
    if (!pinnedJobs.length) {
      return (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton className="pointer-events-none text-slate-500">
            No pinned jobs
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }
    return pinnedJobs.map((pinnedJob) => (
      <TooltipProvider delayDuration={0} key={pinnedJob.jobId}>
        <Tooltip>
          <TooltipTrigger asChild>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild>
                <Link
                  to={`companies/${pinnedJob.companyId}/jobs/${pinnedJob.jobId}`}
                  onClick={handlePinnedJobClick}
                >
                  <Text truncate variant="secondary" className="text-[13px]">
                    {pinnedJob.companyAbbreviation
                      ? `${pinnedJob.companyAbbreviation} - ${pinnedJob.title}`
                      : pinnedJob.title}
                  </Text>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>
              {pinnedJob.companyName} - {pinnedJob.title}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ));
  };

  const render = () => {
    return (
      <SidebarGroup className={className}>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={handleToggle} tooltip="Pinned Jobs">
                <Pin2Icon />
                <Text className="text-sm font-light text-inherit">
                  Pinned jobs
                </Text>
                <ChevronRightIcon
                  className={cn(
                    "ml-auto transition-transform duration-200",
                    pinnedJobsCollapsibleOpen && "rotate-90",
                  )}
                />
              </SidebarMenuButton>
              {pinnedJobsCollapsibleOpen && (
                <div className="group-data-[collapsible=icon]:hidden">
                  <SidebarMenuSub className="mr-1 pr-1">
                    {renderListContent()}
                  </SidebarMenuSub>
                </div>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return render();
};
